// hooks/useSocketListeners.ts
import { useEffect } from "react";
import { Socket } from "socket.io-client";
import EndpointsEnum from "@models/EndpointsEnum"; // Adjust path as necessary
import ISupplierModel from "@models/ISupplierModel"; // Adjust path as necessary

// Define the types for the handlers this hook will receive
import IOnBulkOrderSubmitDTO from "@models/DTO/IOnBulkOrderSubmitDTO"; // Adjust path
import IBulkOrderSubmitStartDTO from "@models/DTO/IBulkOrderSubmitStartDTO"; // Adjust path
import IBulkOrderSubmitProgressDTO from "@models/DTO/IBulkOrderSubmitProgressDTO"; // Adjust path
import IOnValidateBulkOrderSubmitDTO from "@models/DTO/IOnValidateBulkOrderSubmitDTO"; // Adjust path
import IOnGetShipstationCarriersDTO from "@models/DTO/IOnGetShipstationCarriersDTO"; // Adjust path
import IOnGetShipstationServicesForCarrierDTO from "@models/DTO/IOnGetShipstationServicesForCarrierDTO"; // Adjust path
import IOnGetShipstationPackageTypesForCarrierDTO from "@models/DTO/IOnGetShipstationPackageTypesForCarrierDTO"; // Adjust path
import INotification from "@models/INotification";

interface SocketHandlers {
    onBulkOrderSubmit: (data: IOnBulkOrderSubmitDTO) => void;
    bulkOrderSubmitStart: (data: IBulkOrderSubmitStartDTO) => void;
    bulkOrderSubmitProgress: (data: IBulkOrderSubmitProgressDTO) => void;
    onValidateBulkOrderSubmit: (data: IOnValidateBulkOrderSubmitDTO) => void;
    onGetShipstationCarriers: (data: IOnGetShipstationCarriersDTO) => void;
    onGetShipstationServicesForCarrier: (data: IOnGetShipstationServicesForCarrierDTO) => void;
    onGetShipstationPackageTypesForCarrier: (data: IOnGetShipstationPackageTypesForCarrierDTO) => void;
    notifyFn: (notification: INotification) => void;
}

interface UseSocketListenersProps {
    socket: Socket;
    supplierModel: ISupplierModel;
    handlers: SocketHandlers;
}

const useBulkOrderSockets = ({ socket, supplierModel, handlers }: UseSocketListenersProps) => {
    useEffect(() => {
        // Destructure handlers for easier access inside the effect
        const {
            onBulkOrderSubmit,
            bulkOrderSubmitStart,
            bulkOrderSubmitProgress,
            onValidateBulkOrderSubmit,
            onGetShipstationCarriers,
            onGetShipstationServicesForCarrier,
            onGetShipstationPackageTypesForCarrier,
            notifyFn,
        } = handlers;

        // Set up listeners
        socket.on(EndpointsEnum.OrderEntryOnBulkOrderSubmit, onBulkOrderSubmit);
        socket.on(EndpointsEnum.OrderEntryBulkOrderSubmitStart, bulkOrderSubmitStart);
        socket.on(EndpointsEnum.OrderEntryBulkOrderSubmitProgress, bulkOrderSubmitProgress);
        socket.on(EndpointsEnum.OrderEntryOnValidateBulkOrderSubmit, onValidateBulkOrderSubmit);
        socket.on(EndpointsEnum.Notify, notifyFn);

        if (supplierModel.Supplier_Integration.has_shipstation) {
            socket.on(EndpointsEnum.ShipstationOnGetCarriers, onGetShipstationCarriers);
            socket.on(EndpointsEnum.ShipstationOnGetServicesForCarriers, onGetShipstationServicesForCarrier);
            socket.on(EndpointsEnum.ShipstationOnGetPackageTypesForCarriers, onGetShipstationPackageTypesForCarrier);
        }

        // Cleanup function (runs on unmount)
        return () => {
            socket.off(EndpointsEnum.OrderEntryOnBulkOrderSubmit, onBulkOrderSubmit);
            socket.off(EndpointsEnum.OrderEntryBulkOrderSubmitStart, bulkOrderSubmitStart);
            socket.off(EndpointsEnum.OrderEntryBulkOrderSubmitProgress, bulkOrderSubmitProgress);
            socket.off(EndpointsEnum.OrderEntryOnValidateBulkOrderSubmit, onValidateBulkOrderSubmit);
            socket.off(EndpointsEnum.Notify, notifyFn);

            if (supplierModel.Supplier_Integration.has_shipstation) {
                socket.off(EndpointsEnum.ShipstationOnGetCarriers, onGetShipstationCarriers);
                socket.off(EndpointsEnum.ShipstationOnGetServicesForCarriers, onGetShipstationServicesForCarrier);
                socket.off(EndpointsEnum.ShipstationOnGetPackageTypesForCarriers, onGetShipstationPackageTypesForCarrier);
            }
        };
    }, [
        socket,
        supplierModel,
        // Handlers must be stable references (use useCallback in the consuming component)
        handlers.onBulkOrderSubmit,
        handlers.bulkOrderSubmitStart,
        handlers.bulkOrderSubmitProgress,
        handlers.onValidateBulkOrderSubmit,
        handlers.onGetShipstationCarriers,
        handlers.onGetShipstationServicesForCarrier,
        handlers.onGetShipstationPackageTypesForCarrier,
        handlers.notifyFn,
    ]); // Dependencies: socket, supplierModel, and the handler functions

    // This hook doesn't return anything visible to the UI
    return null;
};

export default useBulkOrderSockets;
